import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { AlertTriangle, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QuitConfirmation({ open, onClose }) {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [step, setStep] = useState(1);
  const [reason, setReason] = useState("");
  const [confirming, setConfirming] = useState(false);

  const handleReasonSelect = (selectedReason) => {
    setReason(selectedReason);
    setStep(2);
  };

  const handleFinalQuit = async () => {
    setConfirming(true);
    try {
      await axios.post(`${API}/quitter-mode/quit`, { reason });
      logout();
      navigate("/");
    } catch (error) {
      console.error("Failed to process quit:", error);
      logout();
      navigate("/");
    }
  };

  const handleClose = () => {
    setStep(1);
    setReason("");
    onClose();
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="w-full max-w-md"
        >
          {step === 1 && (
            <Card className="bg-zinc-900 border-zinc-800 p-6">
              <div className="flex items-center gap-3 mb-6">
                <AlertTriangle className="h-6 w-6 text-red-500" />
                <h3 className="text-xl font-bold uppercase tracking-tight">
                  Before you quit
                </h3>
              </div>
              
              <p className="text-zinc-400 mb-6">
                Are you quitting because this is hard — or because you don't care anymore?
              </p>

              <div className="space-y-3 mb-6">
                <Button
                  variant="outline"
                  onClick={() => handleReasonSelect("hard")}
                  className="w-full justify-start border-zinc-700 text-zinc-300 hover:border-primary hover:text-white py-4"
                >
                  This is hard
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleReasonSelect("dont_care")}
                  className="w-full justify-start border-zinc-700 text-zinc-300 hover:border-red-500 hover:text-white py-4"
                >
                  I don't care anymore
                </Button>
              </div>

              <Button
                variant="ghost"
                onClick={handleClose}
                className="w-full text-zinc-500 hover:text-white"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Go back
              </Button>
            </Card>
          )}

          {step === 2 && reason === "hard" && (
            <Card className="bg-zinc-900 border-zinc-800 p-6">
              <h3 className="text-xl font-bold uppercase tracking-tight mb-4">
                Hard is allowed.
              </h3>
              
              <p className="text-zinc-400 mb-6">
                This is supposed to be hard. That's how you know it's working. 
                The question isn't whether it's hard — it's whether you're willing to keep going anyway.
              </p>

              <p className="text-zinc-500 text-sm mb-6">
                Everyone who finished felt exactly what you're feeling right now. They just didn't quit.
              </p>

              <div className="space-y-3">
                <Button
                  onClick={handleClose}
                  className="w-full bg-primary hover:bg-primary/90 uppercase font-bold tracking-widest py-4"
                >
                  Keep Going
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setStep(3)}
                  className="w-full text-zinc-600 hover:text-red-500"
                >
                  I still want to quit
                </Button>
              </div>
            </Card>
          )}

          {step === 2 && reason === "dont_care" && (
            <Card className="bg-zinc-900 border-zinc-800 p-6">
              <h3 className="text-xl font-bold uppercase tracking-tight mb-4">
                That's honest.
              </h3>
              
              <p className="text-zinc-400 mb-6">
                Not caring is a decision. So is caring. The door will be here if you change your mind.
              </p>

              <div className="space-y-3">
                <Button
                  variant="outline"
                  onClick={handleClose}
                  className="w-full border-zinc-700 text-zinc-300 hover:border-primary hover:text-white py-4"
                >
                  Actually, I'll stay
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setStep(3)}
                  className="w-full text-zinc-600 hover:text-red-500"
                >
                  Confirm quit
                </Button>
              </div>
            </Card>
          )}

          {step === 3 && (
            <Card className="bg-zinc-900 border-red-900/50 p-6">
              <h3 className="text-xl font-bold uppercase tracking-tight mb-4 text-red-500">
                Final confirmation
              </h3>
              
              <p className="text-zinc-400 mb-6">
                Quitting is a decision. Your progress will be saved if you ever want to return.
              </p>

              <p className="text-zinc-600 text-sm italic mb-6">
                "Hard is allowed. Quitting is a decision."
              </p>

              <div className="space-y-3">
                <Button
                  onClick={handleClose}
                  className="w-full bg-white text-black hover:bg-zinc-200 uppercase font-bold tracking-widest py-4"
                >
                  Don't Quit
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleFinalQuit}
                  disabled={confirming}
                  className="w-full uppercase font-bold tracking-widest py-4"
                >
                  {confirming ? "Processing..." : "Yes, I Quit"}
                </Button>
              </div>
            </Card>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
